import { useState, useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { checkCloudflareConnectivity } from '@/integrations/supabase/client';

interface UseResumeManagerOptions {
  initialWaitTimeMs?: number;  // Initial wait time after resume before checking connectivity
  connectivityTimeoutMs?: number;  // Timeout for connectivity check
  maxRetries?: number;  // Maximum number of retries for connectivity check
  retryDelayMs?: number;  // Delay between retries
  debugMode?: boolean;  // Enable debug logging
}

/**
 * Hook to manage app resume state and ensure network is ready after resume
 * Returns a resumeReady flag that becomes true only after network connectivity
 * has been verified following an app resume
 */
export function useResumeManager({
  initialWaitTimeMs = 300,
  connectivityTimeoutMs = 2000,
  maxRetries = 1,
  retryDelayMs = 500,
  debugMode = true,
}: UseResumeManagerOptions = {}) {
  // State to track if it's safe to make network requests after resume
  const [resumeReady, setResumeReady] = useState(true);
  
  // Track if app is currently resuming
  const [isResuming, setIsResuming] = useState(false);
  
  // Track current state and the time of the last resume
  const [appState, setAppState] = useState<'active' | 'inactive' | 'background'>('active');
  const lastResumeTime = useRef<number>(0);
  
  // Lock to prevent multiple resume handlers from running in parallel
  const isProcessingResumeRef = useRef(false);
  
  // Log function that respects debug mode
  const log = (message: string, ...args: any[]) => {
    if (debugMode) {
      
    }
  };

  useEffect(() => {
    // Only add listeners on iOS native platform
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      log('Not running on iOS native platform, resume management disabled');
      return;
    }

    log('Initializing resume manager');

    // Handle app state changes and resume events
    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      const newState = isActive ? 'active' : 'background';
      const prevState = appState;
      
      log(`App state changed: ${prevState} -> ${newState}`);
      setAppState(newState);
      
      // Only handle resume (background -> active) transitions
      if (prevState === 'background' && newState === 'active') {
        handleAppResume();
      }
    };

    // Handle app resume
    const handleAppResume = async () => {
      // Prevent multiple handlers from running in parallel
      if (isProcessingResumeRef.current) {
        log('Resume already being processed, ignoring duplicate event');
        return;
      }
      
      try {
        isProcessingResumeRef.current = true;
        
        log('App resumed from background, preparing network verification');
        setIsResuming(true);
        setResumeReady(false);
        
        // Record resume time
        lastResumeTime.current = Date.now();
        
        // Initial wait to let iOS networking stack get ready
        log(`Waiting ${initialWaitTimeMs}ms before checking network connectivity`);
        await new Promise(resolve => setTimeout(resolve, initialWaitTimeMs));
        
        // Verify network connectivity with retries
        let isConnected = false;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          log(`Checking network connectivity (attempt ${attempt}/${maxRetries})`);
          
          try {
            isConnected = await checkCloudflareConnectivity(connectivityTimeoutMs);
            
            if (isConnected) {
              log(`Network connectivity verified on attempt ${attempt}`);
              break;
            } else {
              log(`Network connectivity check failed on attempt ${attempt}`);
              
              if (attempt < maxRetries) {
                log(`Waiting ${retryDelayMs}ms before retry`);
                await new Promise(resolve => setTimeout(resolve, retryDelayMs));
              }
            }
          } catch (error) {
            log(`Error during connectivity check (attempt ${attempt}):`, error);
            
            if (attempt < maxRetries) {
              log(`Waiting ${retryDelayMs}ms before retry`);
              await new Promise(resolve => setTimeout(resolve, retryDelayMs));
            }
          }
        }
        
        // Set resumeReady based on connectivity result
        setResumeReady(isConnected);
        
        if (isConnected) {
          log('Resume process complete - network is ready');
        } else {
          log('Resume process complete - network connectivity could not be verified');
        }
      } finally {
        setIsResuming(false);
        isProcessingResumeRef.current = false;
      }
    };

    // Subscribe to app state events
    const appStateListener = App.addListener('appStateChange', handleAppStateChange);
    
    // Clean up listeners
    return () => {
      appStateListener.remove();
    };
  }, [appState, initialWaitTimeMs, connectivityTimeoutMs, maxRetries, retryDelayMs, debugMode]);

  return {
    resumeReady,
    isResuming,
    appState,
    lastResumeTime: lastResumeTime.current,
  };
} 